import React, { FC } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ContentChannelOption } from "./ChannelMultiSelect";

/**
 * Picker for the 6 generic content channels. A project may carry the SAME
 * channel multiple times (e.g. 2× PDP), so state is an EXPANDED list — one
 * array element per channel instance — which keeps downstream code
 * (`channel_ids` payloads, pricing preview) working with duplicates for free.
 *
 * Click a chip (or its +) to add an instance; − removes one. Removal prefers
 * entries without a `brand_project_channel_id` (not yet persisted) so
 * existing instances — which may have delivered files — are trimmed last;
 * the backend still refuses to drop protected instances.
 */
interface ChannelQuantityPickerProps {
    channels: ContentChannelOption[];
    selectedChannels: ContentChannelOption[];
    onChange: (channels: ContentChannelOption[]) => void;
    loading?: boolean;
}

export const ChannelQuantityPicker: FC<ChannelQuantityPickerProps> = ({
    channels,
    selectedChannels,
    onChange,
    loading,
}) => {
    if (loading) {
        return (
            <Typography sx={{ fontSize: 13, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                Loading channels…
            </Typography>
        );
    }

    if (!channels.length) {
        return (
            <Box
                sx={{
                    p: "10px 12px",
                    background: "var(--surface-2)",
                    borderRadius: "12px",
                    border: "1px solid var(--border)",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "var(--muted)", fontFamily: "CircularStd" }}>
                    No channels available for this content type.
                </Typography>
            </Box>
        );
    }

    const countFor = (ch: ContentChannelOption) =>
        selectedChannels.filter((s) => s.id === ch.id).length;

    const addOne = (ch: ContentChannelOption) => {
        onChange([...selectedChannels, { id: ch.id, name: ch.name }]);
    };

    const removeOne = (ch: ContentChannelOption) => {
        const entries = selectedChannels.filter((s) => s.id === ch.id);
        if (entries.length === 0) return;
        // Prefer dropping an unsaved entry; fall back to the last saved one.
        const unsaved = entries.filter((e) => !e.brand_project_channel_id);
        const target = unsaved.length > 0 ? unsaved[unsaved.length - 1] : entries[entries.length - 1];
        const idx = selectedChannels.lastIndexOf(target);
        onChange(selectedChannels.filter((_, i) => i !== idx));
    };

    return (
        <Box
            sx={{
                background: "var(--surface-2)",
                borderRadius: "12px",
                border: "1px solid var(--border)",
                padding: "10px 12px",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
            }}
        >
            {channels.map((ch) => {
                const count = countFor(ch);
                const sel = count > 0;
                return (
                    <Box
                        key={ch.id}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: sel ? "var(--accent)" : "var(--surface)",
                            border: `1px solid ${sel ? "var(--accent)" : "var(--border)"}`,
                            borderRadius: "99px",
                            height: "30px",
                            transition: "background-color .12s, border-color .12s",
                            "&:hover": {
                                // #8a6e3f: the darker-gold hover has no token (--accent-dark is lighter); gold is theme-constant so it holds in both modes
                                background: sel ? "#8a6e3f" : "var(--surface-2)",
                            },
                        }}
                    >
                        {sel && (
                            <IconButton
                                size="small"
                                onClick={() => removeOne(ch)}
                                sx={{ color: "#fff", p: "2px", ml: "4px" }}
                                aria-label={`Remove one ${ch.name}`}
                            >
                                <RemoveIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        )}
                        <Chip
                            label={count > 1 ? `${ch.name} ×${count}` : ch.name}
                            onClick={() => addOne(ch)}
                            size="small"
                            sx={{
                                fontFamily: "CircularStd",
                                fontSize: 12.5,
                                fontWeight: sel ? 600 : 500,
                                background: "transparent",
                                color: sel ? "#fff" : "var(--text)",
                                border: "none",
                                height: "28px",
                                cursor: "pointer",
                                "& .MuiChip-label": { px: sel ? "6px" : "10px" },
                                "&:hover": { background: "transparent" },
                            }}
                        />
                        {sel && (
                            <IconButton
                                size="small"
                                onClick={() => addOne(ch)}
                                sx={{ color: "#fff", p: "2px", mr: "4px" }}
                                aria-label={`Add another ${ch.name}`}
                            >
                                <AddIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};
